import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import QuestionSection from "../../ui/QuestionSection/QuestionSection";
import CommonFooter from "../../CommonFooter/CommonFooter";
import classes from "./PreExaminationProcess.module.css";
import { env } from "../../../constants/env";
import axios from "axios";
import CommonButton from "../../ui/CommonButton/CommonButton";

const PreExaminationProcess = () => {
  const { labId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [currentSet, setCurrentSet] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isfetching, setIsFetching] = useState(false);

  const userRole = user?.role;

  const [editable, setEditable] = useState(userRole === "ASSESSOR");

  const handleAnswerChange = (questionId, score, comment, points) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { score, comment, points },
    }));
  };

  useEffect(() => {
    if (!labId) return;
    fetchAccreditation();
  }, [labId, token, user._id]);

  const fetchAccreditation = async () => {
    setIsFetching(true);
    axios
      .get(`${env.kataApiUrl}/api/accreditation`, {
        params: {
          labId,
          section: "Pre examination process",
          name: "SADCAS F134-A",
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data.success && res.data.accreditation) {
          const accreditation = res.data.accreditation;

          // Check if current user is the assigned auditor
          if (accreditation.auditorId !== user._id || userRole !== "ASSESSOR") {
            setEditable(false);
          }

          if (accreditation.answers) {
            // Prefill answers
            const savedAnswers = accreditation.answers || [];
            const mapped = savedAnswers.reduce((acc, a) => {
              acc[a.questionId] = {
                score: a.score || "",
                comment: a.comment || "",
                points: a.points || 0,
              };
              return acc;
            }, {});
            setAnswers(mapped);
            setEditable(false); // ✅ answers exist → read-only by default
          } else {
            setEditable(true); // ✅ no answers → open in edit mode
          }
        }
      })
      .catch((err) => {
        console.error("Failed to fetch accreditation:", err);
      })
      .finally(() => {
        setIsFetching(false);
      });
  };

  const questionSets = [
    {
      questions: [
        {
          content: "General",
          number: "7.2.4.1",
          hideOptions: true,
        },
        {
          questionId: "Q1",
          content:
            "Does the laboratory have procedures for the collection and handling of primary samples?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q2",
          content:
            "Is the information available to those responsible for sample collection?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q3",
          content:
            "Is any deviation from the established collection procedures clearly recorded?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q4",
          content:
            "Is the potential risk and impact on the patient outcome of acceptance or rejection of the sample assessed, recorded and communicated to the appropriate personnel?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q5",
          content:
            "Does the laboratory periodically review requirements for sample volume, collection device and preservatives for all sample types, as applicable, to ensure that neither insufficient nor excessive amounts of sample are collected, and samples are properly collected to preserve the analyte?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
        },
      ],
      render: (onAnswerChange, answers) => (
        <QuestionSection
          number="7.2.4"
          title="Primary sample collection and handling"
          addWidth={true}
          accreditation
          showPointsField={false}
          questions={questionSets[0].questions}
          onAnswerChange={editable ? onAnswerChange : () => {}}
          answers={answers}
          disabled={!editable}
        />
      ),
    },
    // Add more sections here if needed
  ];

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet((prev) => prev + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet((prev) => prev - 1);
  };

  const handleDone = async () => {
    setIsSubmitting(true);
    try {
      const answersArray = Object.entries(answers).map(([questionId, data]) => {
        // flatten all questions (questions1..questions6) into one array
        const allQuestions = questionSets.flatMap((set) =>
          Object.keys(set)
            .filter((k) => k.startsWith("questions"))
            .flatMap((k) => set[k])
        );

        const questionContent =
          allQuestions.find((q) => q.questionId === questionId)?.content || "";

        return {
          questionId,
          content: questionContent, // <-- now it gets saved
          score: data.score || undefined,
          comment: data.comment,
          points: data.points,
        };
      });

      const response = await fetch(`${env.kataApiUrl}/api/accreditation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: "SADCAS F134-A",
          labId: labId,
          section: "Pre examination process",
          auditorId: user._id,
          answers: answersArray,
        }),
      });

      if (!response.ok) throw new Error("Submission failed");

      fetchAccreditation();

      toast.success("Updated successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to submit assessment");
    } finally {
      setIsSubmitting(false); // stop loading
    }
  };

  if (isfetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
    <div
      className={`${classes.wrapper} ${!editable ? classes.fromParams : ""}`}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div
          className="d-flex align-items-center gap-2 mb-4"
          onClick={() => navigate(-1)}
          role="button"
        >
          <IoIosArrowRoundBack size={35} />
          <h2 className="m-0">Clause 7: Pre-examination process</h2>
        </div>
        {userRole === "ASSESSOR" && !editable && (
          <CommonButton
            className={classes.editButton}
            onClick={() => setEditable(true)}
            title="Edit"
          />
        )}
      </div>

      {!editable ? (
        <div className={classes.infoText}>
          ✅ Your SADCAS A - Clause 7: Pre-examination process has been
          submitted.
        </div>
      ) : (
        questionSets[currentSet].render(handleAnswerChange, answers)
      )}

      {editable && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => {
            setEditable(false);
          }}
          currentSet={currentSet}
          questionSets={questionSets}
          doneText={isSubmitting ? "Submitting..." : "Update"}
          disabled={isSubmitting}
        />
      )}
    </div>
  );
};

export default PreExaminationProcess;
